import React, { useState } from "react";

type DateMode = "same" | "spread";

type BulkOperationsToolbarProps = {
  onExportCSV: () => void;
  onBulkSetDates: (startDate: string, endDate: string) => void;
  onSpreadDates: (rangeStart: string, rangeEnd: string) => void;
  filterText: string;
  onFilterChange: (text: string) => void;
  sectionCount?: number;
};

export default function BulkOperationsToolbar({
  onExportCSV,
  onBulkSetDates,
  onSpreadDates,
  filterText,
  onFilterChange,
  sectionCount = 0,
}: BulkOperationsToolbarProps) {
  const [showDateModal, setShowDateModal] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState("");
  const [bulkEndDate, setBulkEndDate] = useState("");
  const [dateMode, setDateMode] = useState<DateMode>("spread");

  const handleApply = () => {
    if (!bulkStartDate || !bulkEndDate) return;

    if (dateMode === "same") {
      onBulkSetDates(bulkStartDate, bulkEndDate);
    } else {
      onSpreadDates(bulkStartDate, bulkEndDate);
    }

    setShowDateModal(false);
    setBulkStartDate("");
    setBulkEndDate("");
  };

  // Calculate preview info for spread mode
  const getSpreadPreview = () => {
    if (!bulkStartDate || !bulkEndDate || sectionCount === 0) return null;
    const start = new Date(bulkStartDate);
    const end = new Date(bulkEndDate);
    const totalDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (totalDays < 1) return null;
    const daysPerSection = totalDays / sectionCount;
    return { totalDays, daysPerSection: Math.round(daysPerSection * 10) / 10 };
  };

  const preview = dateMode === "spread" ? getSpreadPreview() : null;

  return (
    <>
      <div className="lmb-bulk-operations">
        <div className="lmb-bulk-left">
          <input
            type="text"
            className="lmb-input lmb-filter-input"
            placeholder="🔍 Filter sections..."
            value={filterText}
            onChange={(e) => onFilterChange(e.target.value)}
          />
        </div>
        <div className="lmb-bulk-right">
          <button
            className="lmb-btn lmb-btn-sm"
            onClick={() => setShowDateModal(true)}
            type="button"
            title="Set dates for all sections"
          >
            📅 Set Dates
          </button>
          <button
            className="lmb-btn lmb-btn-sm"
            onClick={onExportCSV}
            type="button"
            title="Export to CSV"
          >
            📤 Export CSV
          </button>
        </div>
      </div>

      {/* Bulk Date Setting Modal */}
      {showDateModal && (
        <div className="lmb-modal-overlay" onClick={() => setShowDateModal(false)}>
          <div className="lmb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lmb-modal-header">
              <h3>Set Dates for All Sections</h3>
              <button
                className="lmb-modal-close"
                onClick={() => setShowDateModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="lmb-modal-body">
              {/* Mode toggle */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <button
                  className={`lmb-btn lmb-btn-sm ${dateMode === "spread" ? "lmb-btn-primary" : ""}`}
                  onClick={() => setDateMode("spread")}
                  type="button"
                  style={{ flex: 1 }}
                >
                  Spread across range
                </button>
                <button
                  className={`lmb-btn lmb-btn-sm ${dateMode === "same" ? "lmb-btn-primary" : ""}`}
                  onClick={() => setDateMode("same")}
                  type="button"
                  style={{ flex: 1 }}
                >
                  Same dates for all
                </button>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="lmb-muted lmb-label">
                  {dateMode === "spread" ? "Range Start" : "Start Date"}
                </label>
                <input
                  type="date"
                  className="lmb-input"
                  value={bulkStartDate}
                  onChange={(e) => setBulkStartDate(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="lmb-muted lmb-label">
                  {dateMode === "spread" ? "Range End" : "End Date"}
                </label>
                <input
                  type="date"
                  className="lmb-input"
                  value={bulkEndDate}
                  onChange={(e) => setBulkEndDate(e.target.value)}
                />
              </div>
              <p className="lmb-muted" style={{ fontSize: 13 }}>
                {dateMode === "same" ? (
                  "This will set the same start and end dates for all sections."
                ) : preview ? (
                  <>Each section will get ~{preview.daysPerSection} days across {preview.totalDays} total days ({sectionCount} sections).</>
                ) : (
                  "Dates will be distributed evenly across all sections in chapter order."
                )}
              </p>
            </div>
            <div className="lmb-modal-footer">
              <button
                className="lmb-btn"
                onClick={() => setShowDateModal(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="lmb-btn lmb-btn-primary"
                onClick={handleApply}
                disabled={!bulkStartDate || !bulkEndDate}
                type="button"
              >
                {dateMode === "same" ? "Apply to All" : "Spread Dates"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
